"use client";

import { Select } from "@dxp-feed/ui";
import React, { useState, useEffect, useRef } from "react";

interface DatePickerProps {
  range?: boolean | "weekly";
  onDateChange?: (selectedDate: {
    startDate: Date | null;
    endDate: Date | null;
    startDateStr: string;
    endDateStr: string;
    timeZone: string;
  }) => void;
  timeZone?: string;
  icon?: React.ReactNode;
  datePickerClassName?: string;
  className?: string;
  HeaderClass?: string;
  DaysClass?: string;
  BodyClass?: string;
  defaultDate?: Date;
  // New disabled dates props
  disabledDates?: Date[] | ((date: Date) => boolean);
  minDate?: Date;
  maxDate?: Date;
}

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th"; // Covers 4-20 (special case)
  const lastDigit = day % 10;
  switch (lastDigit) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatDate = (date: Date | null) => {
  if (!date) return "";
  const month = date.toLocaleString("en-US", { month: "short" }); // "Feb"
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

const getTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Utility function to get the start and end of the week
const getWeekRange = (date: Date) => {
  const start = new Date(date);
  const end = new Date(date);

  // Set start to the previous Monday
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day; // Sunday becomes -6, Monday becomes 0, etc.
  start.setDate(date.getDate() + diffToMonday);

  // Set end to the following Sunday
  const diffToSunday = day === 0 ? 0 : 7 - day;
  end.setDate(date.getDate() + diffToSunday);

  return { start, end };
};

function getLocalDate(defaultDate: string | Date): Date {
  if (defaultDate instanceof Date) {
    // If it's already a Date, create a new Date with the same Y/M/D parts to avoid timezone issues
    return new Date(
      defaultDate.getFullYear(),
      defaultDate.getMonth(),
      defaultDate.getDate()
    );
  } else if (typeof defaultDate === "string") {
    // If it's a string, parse it and create local Date
    const [year, month, day] = defaultDate.split("-").map(Number);
    return new Date(year, month - 1, day);
  } else {
    throw new Error("Invalid date format");
  }
}

export const DatePicker: React.FC<DatePickerProps> = ({
  range = true,
  onDateChange,
  timeZone,
  icon,
  className,
  datePickerClassName,
  HeaderClass,
  DaysClass,
  BodyClass,
  defaultDate,
  disabledDates,
  minDate,
  maxDate,
}) => {
  const initialStartDate = defaultDate ? getLocalDate(defaultDate) : null;
  const initialEndDate =
    defaultDate && range === "weekly"
      ? getWeekRange(getLocalDate(defaultDate)).end
      : null;

  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCloseIcon, setShowCloseIcon] = useState(false);
  const [clearData, setClearData] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultDate) {
      setCurrentDate(getLocalDate(defaultDate));
    } else {
      setCurrentDate(new Date());
    }
  }, [defaultDate]);

  // Function to check if a date is disabled
  const isDateDisabled = (date: Date): boolean => {
    // Check min/max date constraints
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;

    // Check custom disabled dates
    if (disabledDates) {
      if (typeof disabledDates === "function") {
        return disabledDates(date);
      } else if (Array.isArray(disabledDates)) {
        return disabledDates.some(
          (disabledDate) => date.toDateString() === disabledDate.toDateString()
        );
      }
    }

    return false;
  };

  const displayText = () => {
    if (startDate) {
      if (range) {
        return `${formatDate(startDate)}${endDate ? ` - ${formatDate(endDate)}` : ""}`;
      }
      return formatDate(startDate);
    }

    // Handling when defaultDate is NOT provided
    if (range === "weekly" || range === true) {
      return "Select Date Range";
    }
    return "Select Date";
  };

  const clearSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentDate(new Date());
    setHoverDate(null);
    onDateChange?.({
      startDate: null,
      endDate: null,
      startDateStr: "",
      endDateStr: "",
      timeZone: getTimeZone(),
    });
    setClearData(true);
  };

  useEffect(() => {
    if (!clearData && defaultDate && range === "weekly") {
      const dateFromString = getLocalDate(defaultDate);
      const { start, end } = getWeekRange(dateFromString);

      setStartDate(start);
      setEndDate(end);
    }
    if (clearData) {
      setClearData(false);
    }
  }, [defaultDate, range]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen && startDate && !endDate) {
      setEndDate(startDate);
    }
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleDateClick = (date: Date) => {
    // Don't allow selection of disabled dates
    if (isDateDisabled(date)) {
      return;
    }

    let newStartDate = startDate;
    let newEndDate = endDate;

    if (!range) {
      newStartDate = date;
      newEndDate = null;
      setIsOpen(false); // Close popup immediately after selection
    } else if (range === "weekly") {
      const { start, end } = getWeekRange(date);
      // For weekly selection, check if any day in the week is disabled
      const weekDays = [];
      const current = new Date(start);
      while (current <= end) {
        weekDays.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      // If any day in the week is disabled, don't allow selection
      if (weekDays.some((day) => isDateDisabled(day))) {
        return;
      }

      newStartDate = start;
      newEndDate = end;
      setIsOpen(false); // Close the date picker after selecting the week
    } else {
      if (!newStartDate || newEndDate) {
        newStartDate = date;
        newEndDate = null;
      } else if (newStartDate && !newEndDate) {
        if (date >= newStartDate) {
          newEndDate = date;
        } else {
          newStartDate = date;
          newEndDate = null;
        }
      }

      if (newStartDate && newEndDate) {
        setIsOpen(false);
      }
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);

    onDateChange?.({
      startDate: newStartDate,
      endDate: newEndDate,
      startDateStr: formatDate(newStartDate),
      endDateStr: newEndDate ? formatDate(newEndDate) : "",
      timeZone: getTimeZone(),
    });
  };

  const [shouldFlip, setShouldFlip] = useState(false);

  const selectRef = useRef<HTMLDivElement>(null);

  const handleDateHover = (date: Date) => {
    if (isDateDisabled(date)) {
      return;
    }
    setHoverDate(date);
  };
  const handleDateLeave = () => {
    setHoverDate(null);
  };
  // Helper function to check if date is in hover preview range
  const isInHoverRange = (date: Date): boolean => {
    if (!hoverDate) return false;
    if (range === "weekly") {
      const { start, end } = getWeekRange(hoverDate);
      return date >= start && date <= end;
    } else if (range && startDate && !endDate) {
      const minDate = startDate < hoverDate ? startDate : hoverDate;
      const maxDate = startDate < hoverDate ? hoverDate : startDate;
      return date >= minDate && date <= maxDate;
    }
    return false;
  };

  const renderCalendar = () => {
    // Step 1: Get current month days
    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();

    const firstDay =
      (new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() +
        6) %
      7;

    // Step 2: Add leading days from previous month
    const leadingDays: Date[] = [];
    if (firstDay > 0) {
      const prevMonthLastDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        0
      );
      const prevMonthLastDay = prevMonthLastDate.getDate();

      for (let i = firstDay - 1; i >= 0; i--) {
        leadingDays.push(
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            prevMonthLastDay - i
          )
        );
      }
    }

    // Step 3: Current month days
    const currentMonthDays: Date[] = Array.from(
      { length: daysInMonth },
      (_, i) => {
        return new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          i + 1
        );
      }
    );

    // Step 4: Trailing days from next month to complete the last week
    const totalDisplayedDays = leadingDays.length + currentMonthDays.length;
    const remainder = totalDisplayedDays % 7;
    const trailingDays: Date[] = [];

    if (remainder !== 0) {
      for (let i = 1; i <= 7 - remainder; i++) {
        trailingDays.push(
          new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i)
        );
      }
    }

    // Step 5: Final array
    const daysArray = [...leadingDays, ...currentMonthDays, ...trailingDays];

    const checkDropdownPosition = () => {
      if (selectRef.current) {
        // Return position and size of the element relative to the viewport.
        const rect = selectRef.current.getBoundingClientRect();
        //  Distance between the bottom of <Select> and the bottom of the viewport
        const spaceBelow = window.innerHeight - rect.bottom;
        // Distance between the top of <Select> and the top of the viewport.
        const spaceAbove = rect.top;
        //If there is less than 200px of space below and at least 200px of space above, then flip the dropdown to open above
        setShouldFlip(spaceBelow < 200 && spaceAbove > 200);
      }
    };
    // Prepare options
    // const yearOptions: any = Array.from({ length: 20 }).map((_, i) => {
    //   const year = new Date().getFullYear() - 5 + i;
    //   return { optionname: String(year), optionval: String(year) };
    // });

   {/* const startYear = 2020;
    const endYear = new Date().getFullYear() + 10;

    const yearOptions: any[] = Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => {
        const year = startYear + i;
        return { optionname: String(year), optionval: String(year) };
      }
    );

    const handleChange = (value: string) => {
      setCurrentDate(new Date(+value, currentDate.getMonth(), 1));
    };*/}
    return (
      <div
        className={`bg-brandWhite shadow-[0px_0px_4px_rgba(0,0,0,0.25)] rounded-lg absolute top-full w-full z-10 ${className || ""}`}
      >
        {/* Month Navigation */}
        <div
          className={`flex justify-between items-center mb-2 p-2 border-b border-solid border-[#d3cccc] ${HeaderClass || ""}`}
        >
          <button
            className="bg-grey150 rounded-full h-10 w-10 flex items-center justify-center"
            onClick={() =>
              setCurrentDate(
                new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth() - 1,
                  1
                )
              )
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <mask
                id="mask0_22010_13606"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="24"
              >
                <rect width="24" height="24" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_22010_13606)">
                <path
                  d="M14 18L8 12L14 6L15.4 7.4L10.8 12L15.4 16.6L14 18Z"
                  fill="#191919"
                />
              </g>
            </svg>
          </button>
          <div>
            <span className="text-[16px]">
              {currentDate.toLocaleString("default", {
                month: "short",
              })}{" "}
            </span>
            <div className="relative inline-block">
             {String(currentDate.getFullYear())}
              {/* <Select
              ref={selectRef}
                options={yearOptions}
                defaultValue={String(currentDate.getFullYear())}
                onChange={handleChange}
                onMouseDown={checkDropdownPosition}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    checkDropdownPosition();
                  }
                }}
                selectAriaLabel="Select Year"
                className={`w-[67px] ${shouldFlip ? "[&_ul]:bottom-full [&_ul]:top-auto" : ""} flex [&>button]:p-0 [&>button]:border-0 [&>button>span]:text-[16px] [&>button>span:last-child]:right-0
                [&>button>span:last-child>span]:w-[18px]
                [&>button>span:last-child>span]:h-[18px]
                [&>button>span:last-child>span]:text-grey450 
                [&>button>span:last-child>span]:!text-[7px] 
                [&>button>span:last-child>span]:flex 
                [&>button>span:last-child>span]:items-center 
                [&>button>span:last-child>span]:justify-center
                [&>ul>li]:px-[10px] [&>ul>li>svg]:hidden
                [&>ul>li[aria-selected='true']]:px-0
                [&>ul>li[aria-selected='true']]:flex
                [&>ul>li[aria-selected='true']]:tems-center
                [&>ul>li[aria-selected='true']]:justify-center
              `}
                checked={true}
                scroll={true}
                defaultLabel={String(currentDate.getFullYear())}
              /> */}
            </div>
          </div>
          <button
            className="bg-grey150 rounded-full h-10 w-10 flex items-center justify-center"
            onClick={() =>
              setCurrentDate(
                new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth() + 1,
                  1
                )
              )
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <mask
                id="mask0_22010_12781"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="24"
              >
                <rect
                  width="24"
                  height="24"
                  transform="matrix(-1 0 0 1 24 0)"
                  fill="#D9D9D9"
                />
              </mask>
              <g mask="url(#mask0_22010_12781)">
                <path
                  d="M10 18L16 12L10 6L8.6 7.4L13.2 12L8.6 16.6L10 18Z"
                  fill="#191919"
                />
              </g>
            </svg>
          </button>
        </div>

        {/* Days of the Week */}
        <div
          className={`grid grid-cols-7 text-center text-sm mb-2 ${DaysClass || ""}`}
        >
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
            <div key={i} className="text-sm text-[#868D9D]">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className={`grid grid-cols-7 gap-y-[2px] p-1 ${BodyClass || ""}`}>
          {daysArray.map((day) => {
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isStart =
              startDate && day.toDateString() === startDate.toDateString();
            const isEnd =
              endDate && day.toDateString() === endDate.toDateString();
            const isInRange =
              startDate && endDate && day > startDate && day < endDate;
            const isDisabled = isDateDisabled(day);
            const isHoverPreview = isInHoverRange(day);

            const isActualOrHoverStart =
              isStart ||
              (isHoverPreview && hoverDate && range === "weekly"
                ? getWeekRange(hoverDate).start.toDateString() ===
                  day.toDateString()
                : hoverDate &&
                  startDate &&
                  !endDate &&
                  day.toDateString() ===
                    (startDate <= hoverDate
                      ? startDate
                      : hoverDate
                    ).toDateString());

            const isActualOrHoverEnd =
              isEnd ||
              (isHoverPreview && hoverDate && range === "weekly"
                ? getWeekRange(hoverDate).end.toDateString() ===
                  day.toDateString()
                : hoverDate &&
                  startDate &&
                  !endDate &&
                  day.toDateString() ===
                    (startDate <= hoverDate
                      ? hoverDate
                      : startDate
                    ).toDateString());

            let borderRadiusClass = "";

            if (range !== false) {
              if (isActualOrHoverStart && !isDisabled) {
                borderRadiusClass =
                  range === "weekly"
                    ? "rounded-l-lg"
                    : "rounded-tl-lg rounded-bl-lg";
              } else if (isActualOrHoverEnd && !isDisabled) {
                borderRadiusClass =
                  range === "weekly"
                    ? "rounded-r-lg"
                    : "rounded-tr-lg rounded-br-lg";
              }
            } else if (
              !range &&
              (isStart ||
                (hoverDate &&
                  day.toDateString() === hoverDate.toDateString())) &&
              !isDisabled
            ) {
              borderRadiusClass = "rounded-lg";
            }

            return (
              <button
                key={day.toString()}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => handleDateHover(day)}
                onMouseLeave={handleDateLeave}
                disabled={isDisabled}
                className={`h-8 w-full text-center transition-all duration-150
          ${!isCurrentMonth ? "text-gray-400" : ""}
          ${
            isDisabled
              ? "text-gray-400 bg-gray-100 cursor-not-allowed opacity-50"
              : ""
          }
          ${
            (isStart || isEnd || isInRange) && !isDisabled
              ? `bg-[#1E1E1E] text-[#ffffff] ${borderRadiusClass}`
              : ""
          }
          ${
            isHoverPreview && !isStart && !isEnd && !isInRange && !isDisabled
              ? `bg-slate-200 text-[#1E1E1E] ${borderRadiusClass}`
              : ""
          }
          ${
            !isStart && !isEnd && !isInRange && !isHoverPreview && !isDisabled
              ? "hover:bg-slate-200 rounded-lg"
              : ""
          }
        `}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={datePickerRef}
      className={`relative w-full min-w-[320px] max-w-sm mx-auto ${datePickerClassName || ""}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setShowCloseIcon(true)}
        onMouseLeave={() => setShowCloseIcon(false)}
        className="flex items-center gap-2 border h-[56px] rounded-[4px] border-grey350 w-full justify-between p-4"
      >
        <span className="text-[14px] 4xs:text-[16px]">{displayText()}</span>
        {startDate || endDate ? (
          showCloseIcon ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
              className="demo-icon icon-close text-brandBlack text-[8px] w-6 h-6 flex items-center justify-center bg-grey100 rounded-full"
            ></div>
          ) : (
            <span className="demo-icon icon-month-calendar w-6 h-6 text-[18px]"></span>
          )
        ) : (
          <span className="demo-icon icon-month-calendar w-6 h-6 text-[18px]"></span>
        )}
      </button>
  
      {isOpen && renderCalendar()}
    </div>
  );
};
